<template>
  <fn-modal
    class="modal-store-listing-filter"
    max-width="xl"
    full-screen-mobile
    closable
    keep-scroll-position
    :headline-text="$t('storeListing.filter.title')"
    @close="closeModal"
  >
    <fn-accordion>
      <fn-accordion-item class="show-m">
        <template #headline>
          {{ $t('storeListing.filter.groups.radiusAndSize.title') }}
        </template>
        <fn-grid>
          <fn-grid-item
            v-if="mapRadiusOptions"
            columns="12"
            columns-m="6"
            static
          >
            <fn-choices
              :options="mapRadiusOptions"
              :value="mapRadius"
              font-normal
              @update:value="selectRadius"
            />
          </fn-grid-item>
          <fn-grid-item
            v-if="sizeOptions"
            columns="12"
            columns-m="6"
            static
          >
            <fn-choices
              :options="sizeOptions"
              :value="choicesFilterApplied('size')"
              font-normal
              @update:value="selectSize"
            />
          </fn-grid-item>
        </fn-grid>
      </fn-accordion-item>
      <fn-accordion-item
        v-if="mapRadiusOptions"
        class="hide-m"
      >
        <template #headline>
          {{ $t('storeListing.filter.groups.radius.title') }}
        </template>
        <fn-choices
          :options="mapRadiusOptions"
          :value="mapRadius"
          font-normal
          @update:value="selectRadius"
        />
      </fn-accordion-item>
      <fn-accordion-item
        v-if="sizeOptions"
        class="hide-m"
      >
        <template #headline>
          {{ $t('storeListing.filter.groups.size.title') }}
        </template>
        <fn-choices
          :options="sizeOptions"
          :value="choicesFilterApplied('size')"
          font-normal
          @update:value="selectSize"
        />
      </fn-accordion-item>
      <fn-accordion-item v-if="openedStores">
        <template #headline>
          {{ $t('storeListing.filter.groups.status.title') }}
        </template>
        <fn-grid>
          <fn-grid-item
            columns="12"
            columns-m="6"
          >
            <fn-checkbox
              :label="$t('storeListing.filter.groups.status.options.OPENED')"
              :value="filterApplied('status', 'OPENED')"
              @update:value="(checked) => setFilterProperty('status', 'OPENED', checked)"
            />
          </fn-grid-item>
        </fn-grid>
      </fn-accordion-item>
      <fn-accordion-item v-if="getFilterValue('service')">
        <template #headline>
          {{ $t('storeListing.filter.groups.services.title') }}
        </template>
        <fn-grid>
          <fn-grid-item
            v-for="item of getFilterValue('service')"
            :key="item.code"
            columns="12"
            columns-m="6"
          >
            <fn-checkbox
              :label="item.name"
              :value="filterApplied('service', item.code)"
              @update:value="(checked) => setFilterProperty('service', item.code, checked)"
            >
              <template
                v-if="item.description"
                #afterLabel
              >
                <fn-info-i
                  light
                  color="grey-dark"
                >
                  {{ item.description }}
                </fn-info-i>
              </template>
            </fn-checkbox>
          </fn-grid-item>
        </fn-grid>
      </fn-accordion-item>
      <fn-accordion-item v-if="getFilterValue('animal')">
        <template #headline>
          {{ $t('storeListing.filter.groups.animals.title') }}
        </template>
        <fn-grid>
          <fn-grid-item
            v-for="item of getFilterValue('animal')"
            :key="item.code"
            columns="12"
            columns-m="6"
          >
            <fn-checkbox
              :label="item.name"
              :value="filterApplied('animal', item.code)"
              @update:value="(checked) => setFilterProperty('animal', item.code, checked)"
            />
          </fn-grid-item>
        </fn-grid>
      </fn-accordion-item>
    </fn-accordion>
    <fn-grid
      v-if="getFilterValue('onlyActivet') || getFilterValue('onlySalon')"
      class="mslf-section"
    >
      <fn-grid-item
        v-if="getFilterValue('onlyActivet')"
        columns="12"
        columns-m="6"
        static
      >
        <div class="mslf-service mslf-service--activet">
          <fn-checkbox
            ref="serviceActivet"
            class="service-checkbox"
            :value="filterApplied('onlyActivet', 'true')"
            @update:value="(checked) => setFilterProperty('onlyActivet', 'true', checked)"
          />
          <fn-paragraph
            class="service-description"
            v-html="$t('storeListing.filter.groups.activet.title')"
          />
          <fn-image
            class="service-image"
            :src="activetImage.src"
            :width="activetImage.width"
            :height="activetImage.height"
            alt="ActiVet"
          />
        </div>
      </fn-grid-item>
      <fn-grid-item
        v-if="getFilterValue('onlySalon')"
        columns="12"
        columns-m="6"
        static
      >
        <div class="mslf-service mslf-service--salon">
          <fn-checkbox
            ref="serviceSalon"
            class="service-checkbox"
            :value="filterApplied('onlySalon', 'true')"
            @update:value="(checked) => setFilterProperty('onlySalon', 'true', checked)"
          />
          <fn-paragraph
            class="service-description"
            v-html="$t('storeListing.filter.groups.salon.title')"
          />
          <fn-image
            class="service-image"
            :src="salonImage.src"
            :width="salonImage.width"
            :height="salonImage.height"
            alt="Salon"
          />
        </div>
      </fn-grid-item>
    </fn-grid>
    <div class="mslf-footer">
      <fn-button
        secondary
        @click="applyFilters"
      >
        {{ $t('storeListing.filter.applyButton') }}
      </fn-button>
    </div>
  </fn-modal>
</template>

<script>
/* eslint-disable import/no-duplicates */
import ImgActivetLogo from '@/assets/img/logos/activet-logo.png';
import ImgActivetLogoSize from '@/assets/img/logos/activet-logo.png?imageSize';
import ImgSalonLogo from '@/assets/img/logos/salon-logo.png';
import ImgSalonLogoSize from '@/assets/img/logos/salon-logo.png?imageSize';
/* eslint-enable import/no-duplicates */

export default defineComponent({
  props: {
    filterProperties: {
      type: Array,
      default: () => [],
    },
    preAppliedFilters: {
      type: Array,
      default: () => [],
    },
    radius: {
      type: Number,
      default: 20000,
    },
  },
  emits: ['apply', 'close'],
  data: () => ({
    mapRadius: null,
    appliedFilters: [],
  }),
  computed: {
    mapRadiusOptions() {
      return [
        {
          value: 5000,
          label: this.$t('storeListing.filter.groups.radius.label', { radiusKm: 5 }),
        },
        {
          value: 10000,
          label: this.$t('storeListing.filter.groups.radius.label', { radiusKm: 10 }),
        },
        {
          value: 20000,
          label: this.$t('storeListing.filter.groups.radius.label', { radiusKm: 20 }),
        },
        {
          value: 50000,
          label: this.$t('storeListing.filter.groups.radius.label', { radiusKm: 50 }),
        },
        {
          value: 100000,
          label: this.$t('storeListing.filter.groups.radius.label', { radiusKm: 100 }),
        },
      ];
    },

    sizeOptions() {
      const sizes = this.getFilterValue('size');

      if (!sizes || sizes.length <= 0) return false;

      const options = [
        {
          value: null,
          label: this.$t('storeListing.filter.groups.size.options.all'),
        },
      ];

      if (sizes.length >= 2) {
        options.push({
          value: 'XL,XXL',
          label: this.$t('storeListing.filter.groups.size.options.largeAndExtraLarge'),
        });
      }

      options.push({
        value: 'XXL',
        label: this.$t('storeListing.filter.groups.size.options.extraLargeOnly'),
      });

      return options;
    },

    activetImage() {
      return {
        src: ImgActivetLogo,
        ...ImgActivetLogoSize,
      };
    },

    salonImage() {
      return {
        src: ImgSalonLogo,
        ...ImgSalonLogoSize,
      };
    },

    openedStores() {
      const statusValues = this.getFilterValue('status');

      return statusValues.some((status) => status.code === 'OPENED');
    },
  },
  mounted() {
    this.appliedFilters = [...this.preAppliedFilters];
    this.mapRadius = this.radius;
  },
  methods: {
    selectRadius(value) {
      this.mapRadius = +value;
    },

    selectSize(value) {
      const propertyIndex = this.getAppliedFilterIndex('size');

      if (value) {
        const sizes = value.split(',');

        if (propertyIndex <= -1) {
          this.appliedFilters.push({
            property: 'size',
            values: sizes,
          });
          return;
        }
        this.appliedFilters[propertyIndex] = {
          ...this.appliedFilters[propertyIndex],
          values: sizes,
        };
      } else {
        this.removeAppliedFilter(propertyIndex);
      }
    },

    applyFilters() {
      const { appliedFilters, mapRadius } = this;

      this.$emit('apply', {
        appliedFilters,
        radius: mapRadius,
      });
    },

    closeModal() {
      this.$emit('close');
    },

    setFilterProperty(property, value, status) {
      const propertyIndex = this.getAppliedFilterIndex(property);

      if (propertyIndex <= -1) {
        this.appliedFilters.push({
          property,
          values: [value],
        });
        return;
      }

      const propertyValues = [...this.getAppliedFilterValue(property)];

      if (!status) {
        propertyValues.splice(propertyValues.indexOf(value), 1);
      } else {
        propertyValues.push(value);
      }

      if (!propertyValues.length) {
        this.removeAppliedFilter(propertyIndex);
      } else {
        this.appliedFilters[propertyIndex] = {
          ...this.appliedFilters[propertyIndex],
          values: propertyValues,
        };
      }
    },

    getAppliedFilterIndex(filterName) {
      return this.appliedFilters.findIndex((item) => item.property === filterName);
    },

    getAppliedFilterValue(filterName) {
      if (!this.appliedFilters) return null;

      const propertyIndex = this.getAppliedFilterIndex(filterName);

      if (propertyIndex <= -1) return null;

      const property = this.appliedFilters[propertyIndex];

      return property && property.values;
    },

    getFilterValue(filterName) {
      if (!this.filterProperties) {
        return null;
      }

      /**
       * Those filterName values for the single value of values array
       * should have name property defined. If name is not defined
       * the filter checkbox will not be rendered
       */
      const filterItemsWithLabels = ['service', 'animal'];
      const propertyData = this.filterProperties.find((item) => item.property === filterName);
      const propertyDataValues = propertyData?.values?.filter((item) => {
        if (!filterItemsWithLabels.includes(filterName)) {
          return item;
        }

        if (item?.name?.length > 0) {
          return item;
        }

        return null;
      });

      return propertyData && propertyDataValues;
    },

    filterApplied(name, value) {
      const filterValues = this.getAppliedFilterValue(name);

      return filterValues && filterValues.includes(value);
    },

    choicesFilterApplied(name) {
      const filterValues = this.getAppliedFilterValue(name);

      if (!filterValues) return null;

      return filterValues.join(',');
    },

    removeAppliedFilter(index) {
      this.appliedFilters.splice(index, 1);
    },
  },
});
</script>

<style lang="scss" scoped>
.modal-store-listing-filter {
  :deep(.headline-wrapper) {
    position: static;
  }

  .mslf-service {
    display: flex;
    align-items: center;
    padding: $pad;
    border-radius: map-get($border-radiuses, 'xl');

    &.mslf-service--activet {
      background-color: palette(brand, activet);
      color: $color-general-text-inverse-default;
    }

    &.mslf-service--salon {
      background-color: palette(happyColor, blue);
    }

    .service-checkbox {
      margin-right: math.div($pad, 2);
    }

    .service-description {
      flex-grow: 1;
      margin-bottom: 0;
      margin-right: $pad;
    }

    .service-image {
      width: 100px;
    }
  }

  .mslf-section {
    padding: $pad 0;
    border-bottom: 1px solid palette(grey, light);
  }

  .mslf-footer {
    padding-top: $pad;
    text-align: right;
  }
}
</style>
